import { Queryable } from "./queryable"

export enum BillingFrequency {
    Monthly = "Monthly",
    Quarterly = "Quarterly",
    Annually = "Annually",
    OneTime = "OneTime",
}

export enum ExpenseBudgetCurrency {
    CAD = "CAD",
    USD = "USD",
    EUR = "EUR",
}

export enum PlanView {
    Cash = "cash",
    Accounting = "accounting",
}

export enum ExpenseBudgetAlertStatus {
    Open = "Open",
    Acknowledged = "Acknowledged",
    Dismissed = "Dismissed",
}

export interface EmployeeName {
    id: string
    firstName: string
    lastName: string
    commonName: string | null
    companyEmail: string | null
}

export interface CodeDescription {
    id: string
    code: string
    description: string
}

export interface ExpenseBudgetCostCode {
    id: string
    transactionAccountId: string
    projectId: string | null
    approvedAmount: string
    transactionAccount: CodeDescription
    project: CodeDescription | null
}

export interface ExpenseBudgetVendor {
    id: string
    name: string
    // Acumatica vendors (Vendor.AcctCD) whose AP bills belong to this vendor, e.g. Microsoft → LONG VIEW, TTT.
    codes: { vendorCode: string }[]
}

export interface VendorCodeOption {
    vendorCode: string
    vendorName: string
}

// An Acumatica AP bill line (Bill, Debit Adj., Credit Adj., Prepayment, QCK/VQC/RQC quick checks).
// amount is in CAD, signed (credits are negative). isCounted = it counts as actual spend.
export interface VendorBill {
    id: string
    docType: string
    refNbr: string
    lineNbr: number
    vendorCode: string
    vendorRef: string | null
    docDate: string
    fiscalPeriod: number
    status: string
    docDescription: string | null
    lineDescription: string | null
    accountCode: string | null
    projectCode: string | null
    taskCode: string | null
    amount: number
    currency: string
    branchCode: string | null
    isCounted: boolean
    // How the line is allocated to products; several entries = a split. Empty = not linked.
    allocations: BillAllocation[]
}

export interface BillAllocation {
    expenseBudgetLineId: string
    amount: number
}

// An empty allocations list unlinks the bill line.
export interface BillLinkPayload {
    vendorBillLineId: string
    allocations: BillAllocation[]
}

export interface ExpenseBudget {
    id: string
    departmentId: string
    fiscalYearId: number
    ownerId: string
    approvedAmount: string
    inflationRate: string
    defaultTaxRate: string
    alertPercentThreshold: string
    alertAmountThreshold: string
    notes: string | null
    department: CodeDescription
    owner: EmployeeName
    costCodes: ExpenseBudgetCostCode[]
    vendors: ExpenseBudgetVendor[]
    exchangeRates: { currency: ExpenseBudgetCurrency; rateToCad: string }[]
    recipients: { employee: EmployeeName }[]
}

export interface ExpenseBudgetLinePeriod {
    fiscalPeriod: number
    cashAmount: string
    accountingAmount: string
}

export interface ExpenseBudgetLine {
    id: string
    expenseBudgetCostCodeId: string | null
    expenseBudgetVendorId: string
    vendor: ExpenseBudgetVendor
    productName: string
    quantity: string
    currency: ExpenseBudgetCurrency
    previousUnitPrice: string | null
    unitPrice: string
    taxRate: string
    billingFrequency: BillingFrequency
    firstBillingPeriod: number
    renewalDate: string | null
    isMfaEnabled: boolean | null
    accountAdmin: string | null
    paymentMethod: string | null
    notes: string | null
    sortOrder: number
    periods: ExpenseBudgetLinePeriod[]
}

export interface CostCodePlanVsActual {
    id: string
    code: string
    description: string
    approvedAmount: number
    planned: number[]
    actual: number[]
    forecast: number
    isUnmapped: boolean
    // billed = Acumatica bill lines linked to the product in the Mapping tab, per period.
    lines: { id: string; vendorName: string; productName: string; planned: number[]; billed: number[] }[]
}

export interface PlanVsActual {
    fiscalYear: number
    periods: number[]
    firstOpenPeriod: number
    view: PlanView
    costCodes: CostCodePlanVsActual[]
    // Counted Acumatica bills on cost code accounts not linked to a product yet.
    unlinkedBilled: number
}

export interface ExpenseBudgetAlert {
    id: string
    type: string
    fiscalPeriod: number | null
    plannedAmount: string
    actualAmount: string
    message: string
    status: ExpenseBudgetAlertStatus
    comment: string | null
    createdAt: string
    acknowledgedAt: string | null
    acknowledgedBy: EmployeeName | null
    costCode: { transactionAccount: { code: string; description: string } } | null
    line: { productName: string; vendor: { name: string } } | null
}

export interface ExpenseBudgetSearchFields {
    departments: (CodeDescription & { parentId: string | null })[]
    fiscalYears: { id: number }[]
    employees: EmployeeName[]
}

export interface IExpenseBudgetQueryParams extends Queryable {
    departmentIds?: string[]
    fiscalYearId?: number
}

// ---------- Payloads ----------

export interface ExpenseBudgetPayload {
    departmentId?: string
    fiscalYearId?: number
    ownerId: string
    approvedAmount: number
    inflationRate: number
    defaultTaxRate: number
    alertPercentThreshold: number
    alertAmountThreshold: number
    notes?: string
}

export interface CostCodePayload {
    transactionAccountId: string
    projectId: string | null
    approvedAmount: number
}

export interface AccountOption extends CodeDescription {
    amount: number
}

// Previous price, MFA, admin, payment method and notes stay in the DB but are hidden for now.
export interface ExpenseBudgetLinePayload {
    expenseBudgetVendorId: string
    productName: string
    quantity: number
    currency: ExpenseBudgetCurrency
    unitPrice: number
    taxRate: number
    billingFrequency: BillingFrequency
    firstBillingPeriod: number
    renewalDate: string | null
}

export interface VendorPayload {
    name: string
    vendorCodes?: string[]
}

export interface LineMappingPayload {
    expenseBudgetLineId: string
    expenseBudgetCostCodeId: string | null
}

export const employeeName = (employee: EmployeeName | null | undefined): string =>
    employee ? `${employee.commonName ?? employee.firstName} ${employee.lastName}` : "-"
